"use client";

import { useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/button";
import { GoogleSignInButton } from "@/components/google-sign-in-button";
import { InfoCard } from "@/components/info-card";
import { Input } from "@/components/input";
import { CmwLogo } from "@/components/logo";
import { TurnstileWidget, useTurnstile } from "@/components/turnstile";
import { googleErrorMessage } from "@/lib/google-error";
import { getApiErrorMessage } from "@/lib/ky/error-message";
import { authPages, routes, type Role } from "@/lib/routes";
import { useSignInWithPassword } from "../../hooks/use-sign-in-with-password";
import type { AccountType } from "cmw-client";

const accountTypeForRole = (role: Role): AccountType =>
  role === "practitioner" ? "practioner" : "admin";

const adminCopy = {
  heading: "Admin sign in",
  subtitle: "Sign in to the CMW administration portal",
};

const copy: Record<Role, { heading: string; subtitle: React.ReactNode }> = {
  practitioner: {
    heading: "Sign in",
    subtitle: (
      <>
        {"Don't have an account? "}
        <Link href={routes.signUp.url} className="font-semibold text-brand-dark-blue">
          Sign up
        </Link>
      </>
    ),
  },
  staff: adminCopy,
  owner: adminCopy,
};

type SignInFormProps = {
  role: Role;
};

export function SignInForm({ role }: SignInFormProps) {
  const searchParams = useSearchParams();
  const { turnstileRef, takeTurnstileToken } = useTurnstile();
  const signIn = useSignInWithPassword(takeTurnstileToken);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { heading, subtitle } = copy[role];
  const { forgotPassword } = authPages(role);
  const accountType = accountTypeForRole(role);

  const message =
    error ?? googleErrorMessage(searchParams.get("error"), searchParams.get("message"));

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const form = new FormData(event.currentTarget);

    signIn.mutate(
      {
        email: String(form.get("email")),
        password: String(form.get("password")),
        accountType,
      },
      {
        onError: async (cause) =>
          setError(await getApiErrorMessage(cause, "Sign in failed. Please try again.")),
      },
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-438 flex-col items-center gap-24 py-64 lg:py-12">
      <h1 className="hidden lg:block">
        <Link href={routes.home.url} aria-label="Centre for Muslim Wellbeing — home">
          <CmwLogo type="vertical" />
        </Link>
      </h1>

      <div className="flex flex-col items-center gap-8 text-center">
        <h2 className="font-sans text-32 font-semibold leading-[1.3] text-brand-dark-blue">{heading}</h2>
        <p className="text-16 text-neutral-black/70">{subtitle}</p>
      </div>
      <GoogleSignInButton accountType={accountType} intent="sign-in"/>
      <div className="flex w-full items-center gap-8">
        <span className="h-px flex-1 bg-neutral-light-grey" />
        <span className="text-14 text-neutral-black/70">or</span>
        <span className="h-px flex-1 bg-neutral-light-grey" />
      </div>

      <form onSubmit={onSubmit} className="flex w-full flex-col gap-24">
        <div className="flex flex-col gap-16">
          <Input
            id="email"
            name="email"
            type="email"
            required
            label={
              <>
                Email Address
              </>
            }
            placeholder="you@example.com"
          />
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            required
            label={
              <>
                Password
              </>
            }
            placeholder="Enter your password"
            iconRight={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="flex size-24 cursor-pointer items-center justify-center text-neutral-black/70"
              >
                {showPassword ? <EyeSlashIcon className="size-24" /> : <EyeIcon className="size-24" />}
              </button>
            }
          />
          <Link href={forgotPassword} className="text-16 font-medium text-brand-dark-blue">
            Forgot Password?
          </Link>
          {message && (
            <div role="alert">
              <InfoCard title="Sign in failed" description={message} />
            </div>
          )}
        </div>

        <TurnstileWidget ref={turnstileRef} action="sign-in" />

        <Button type="submit" variant="primary-blue" size="l" disabled={signIn.isPending} className="w-full">
          {signIn.isPending ? <ArrowPathIcon className="size-20 animate-spin" /> : "Sign In"}
        </Button>
      </form>

      {/* Practitioner-only: an alternative sign-in method, not to be confused with password
          reset, so it sits below the button rather than beside "Forgot Password?". This same
          component also renders the admin sign-in page, which has no email-code twin. */}
      {role === "practitioner" && (
        <>
          <Link href={routes.signInWithCode.url} className="text-16 font-medium text-brand-dark-blue">
            Sign in with an emailed code instead
          </Link>
          <p className="text-16 text-neutral-black/70">
            {"Are you an Admin? "}
            <Link href={routes.adminSignIn.url} className="font-semibold text-brand-dark-blue">
              Sign-in as an admin
            </Link>
          </p>
        </>
      )}

      <p className="max-w-344 text-center text-12 leading-[1.4] text-neutral-black/50">
        © 2020-2026 Centre for Muslim Wellbeing. All rights reserved.
      </p>
    </div>
  );
}
